import { Heart, HelpCircle, User } from 'lucide-react'

import { FullPageSpinnerPortal } from '@/components/common/Spinner/FullPagePortal'
import { FieldError } from '@/components/ui/field'
import { cn } from '@/lib/utils'

import { CheckinLayout } from './CheckinLayout'
import { StepHeader } from './StepHeader'
import type { GuestItem } from './types'

interface ConfirmStepProps {
  selectedGuest: GuestItem
  onBack: () => void
  confirmForm: any
  submitError: string
  isCheckinPending: boolean
}

export function ConfirmStep({
  selectedGuest,
  onBack,
  confirmForm,
  submitError,
  isCheckinPending,
}: ConfirmStepProps) {
  const checkedInCount = selectedGuest.checkedInCount || 0
  const capacity = selectedGuest.expectedFollowers
  const isFullyCheckedIn = checkedInCount >= capacity
  const isPartiallyCheckedIn = checkedInCount > 0 && !isFullyCheckedIn

  return (
    <CheckinLayout>
      <StepHeader
        title="ยืนยันการเช็กอิน"
        onBack={onBack}
      />

      <div className="mb-6 rounded-2xl border-2 border-white/60 bg-white/70 p-5 shadow-sm">
        <p className="text-foreground/50 mb-1 text-xs font-semibold tracking-widest uppercase">
          ข้อมูลของคุณ
        </p>
        <p className="text-2xl font-semibold">{selectedGuest.fullName}</p>
        {selectedGuest.displayName && (
          <p className="text-foreground/60 mt-0.5 text-base">
            ชื่อเล่น: {selectedGuest.displayName}
          </p>
        )}
        {selectedGuest.group && (
          <p className="text-foreground/60 mt-1 text-sm">
            กลุ่ม: {selectedGuest.group}
          </p>
        )}
        {selectedGuest.side && (
          <p className="text-foreground/60 text-sm">
            ฝ่าย:{' '}
            {selectedGuest.side === 'groom'
              ? 'เจ้าบ่าว'
              : selectedGuest.side === 'bride'
                ? 'เจ้าสาว'
                : 'ไม่ระบุ'}
          </p>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          confirmForm.handleSubmit()
        }}
        className="space-y-4"
      >
        <confirmForm.AppField name="displayName">
          {(field: any) => (
            <field.TextField
              label="ชื่อที่ต้องการให้แสดงในงาน *"
              placeholder="กรอกชื่อที่ต้องการให้แสดง"
            />
          )}
        </confirmForm.AppField>

        <confirmForm.AppField name="maritalStatus">
          {(field: any) => {
            const isInvalid =
              field.state.meta.isTouched && !field.state.meta.isValid
            const options = [
              { value: 'single', label: 'โสด', Icon: User },
              { value: 'not_single', label: 'ไม่โสด', Icon: Heart },
              {
                value: 'prefer_not_to_say',
                label: 'ไม่ระบุ',
                Icon: HelpCircle,
              },
            ]
            return (
              <div className="flex flex-col gap-1.5">
                <p className="text-foreground text-base font-bold">สถานะ *</p>
                <div className="grid grid-cols-3 gap-2">
                  {options.map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => field.handleChange(value)}
                      className={cn(
                        'flex flex-col items-center gap-2 rounded-2xl border-2 px-2 py-4 transition-all active:scale-[0.98]',
                        field.state.value === value
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-white/60 bg-white/60 shadow-sm hover:bg-white/80',
                      )}
                    >
                      <Icon className="size-6" />
                      <span className="text-xs leading-tight font-semibold">
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
                {isInvalid && <FieldError errors={field.state.meta.errors} />}
              </div>
            )
          }}
        </confirmForm.AppField>

        <p className="text-foreground/40 text-center text-xs">
          ข้อมูลนี้จะถูกใช้แสดงผลตลอดงาน
        </p>

        {isPartiallyCheckedIn && (
          <p className="rounded-xl bg-amber-50 p-3 text-center text-sm font-medium text-amber-700">
            มีผู้เช็กอินในชื่อนี้แล้ว {checkedInCount}/{capacity} ท่าน
          </p>
        )}

        {isFullyCheckedIn && (
          <p className="text-destructive bg-destructive/10 rounded-xl p-3 text-center text-sm font-medium">
            เช็กอินครบแล้ว ({checkedInCount}/{capacity} ท่าน)
          </p>
        )}

        {submitError && (
          <p className="text-destructive bg-destructive/10 rounded-xl p-3 text-center text-sm font-medium">
            {submitError}
          </p>
        )}

        {!isFullyCheckedIn && (
          <confirmForm.Subscribe
            selector={(state: any) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }: any) => (
              <button
                type="submit"
                disabled={!canSubmit || isSubmitting || isCheckinPending}
                className="bg-primary text-primary-foreground flex w-full items-center justify-center rounded-2xl py-4 text-base font-semibold shadow-md transition-all active:scale-[0.98] disabled:opacity-60"
              >
                {isSubmitting || isCheckinPending
                  ? 'กำลังดำเนินการ...'
                  : 'ยืนยันเช็กอิน'}
              </button>
            )}
          </confirmForm.Subscribe>
        )}
      </form>
      {isCheckinPending && <FullPageSpinnerPortal />}
    </CheckinLayout>
  )
}
